import { Link } from "react-router-dom";
import { PublicShell, useSeo } from "../glossary/PublicShell";
import { Sparkles, ArrowRight, CheckCircle2, MessageSquareHeart, Search, ShieldCheck, Rocket, Star } from "lucide-react";

const PREVIEW = [
  "Speech therapy", "Occupational therapy", "Psychology & assessment",
  "Behaviour support", "Physiotherapy", "Tutoring & coaching",
];

const PROVIDER_BENEFITS = [
  { icon: Search, title: "Be found by the right families", text: "Parents search by service, city and format — you show up when your support fits." },
  { icon: Star, title: "Featured at launch", text: "Providers who join now are listed first when the Service Finder goes live to families." },
  { icon: ShieldCheck, title: "Free to join", text: "Create your practice profile at no cost. Add services, availability and contact details." },
];

export default function ProvidersIndex() {
  useSeo({
    title: "Autism & Neurodiversity Service Finder — Coming Soon | KidPath Ontario",
    description: "KidPath's Ontario Service Finder for autism and neurodiversity support is launching soon. Providers — speech therapy, OT, psychology, behaviour support and more — can list their practice now, free.",
  });

  return (
    <PublicShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-kid-navy text-white" data-testid="providers-coming-soon">
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #2fb8a8 0, transparent 40%), radial-gradient(circle at 85% 30%, #ffcf5c 0, transparent 35%)" }} />
        <div className="relative max-w-[1180px] mx-auto px-5 md:px-[30px] py-20 sm:py-28">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-2 bg-kid-teal/20 text-kid-teal font-semibold text-sm px-4 py-1.5 rounded-full mb-6" data-testid="coming-soon-badge">
              <Sparkles size={16} /> Coming soon
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
              Ontario's neurodiversity Service Finder is on its way
            </h1>
            <p className="text-white/80 mt-6 text-base sm:text-lg leading-relaxed">
              We're building a trusted directory where families can find autism and neurodiversity providers —
              speech therapy, OT, psychology, behaviour support and more. We're onboarding providers first,
              so every family sees real, ready-to-help practices at launch.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/provider/register" data-testid="coming-soon-provider-cta"
                    className="inline-flex items-center justify-center gap-2 bg-kid-teal text-white font-semibold px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity">
                List your practice — free <ArrowRight size={18} />
              </Link>
              <Link to="/contact" data-testid="coming-soon-contact"
                    className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white/20 transition-colors border border-white/15">
                <MessageSquareHeart size={18} /> Get in touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-16 sm:py-20">
        {/* What's coming preview */}
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-3"><Rocket size={16} /> What families will find</p>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-kid-navy">Search vetted-by-submission providers across Ontario</h2>
          <p className="text-kid-muted mt-3 text-base leading-relaxed">
            When we launch, parents will filter by service, city, format and availability — then reach out directly. Here's a taste of the support you'll be able to find:
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8" data-testid="coming-soon-preview">
          {PREVIEW.map((label) => (
            <div key={label} className="kid-card p-5 flex items-center gap-3 opacity-90">
              <span className="w-9 h-9 rounded-xl bg-kid-skytint grid place-items-center shrink-0"><CheckCircle2 size={18} className="text-kid-teal" /></span>
              <span className="font-poppins font-semibold text-kid-navy text-sm">{label}</span>
            </div>
          ))}
        </div>

        {/* Provider recruitment */}
        <div className="mt-16">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-kid-navy">Are you an Ontario provider?</h2>
            <p className="text-kid-muted mt-3 text-base leading-relaxed">
              Get in front of families the moment we launch. Setting up your profile takes a few minutes and it's completely free.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {PROVIDER_BENEFITS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="kid-card p-6" data-testid="provider-benefit">
                <span className="w-11 h-11 rounded-2xl bg-kid-teal/12 grid place-items-center mb-4"><Icon size={22} className="text-kid-teal" /></span>
                <h3 className="font-display text-lg font-bold text-kid-navy">{title}</h3>
                <p className="text-kid-muted mt-1.5 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-14 rounded-3xl bg-kid-navy text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" data-testid="provider-join-cta">
          <div>
            <h3 className="font-display text-xl sm:text-2xl font-extrabold text-white">Claim your spot before launch</h3>
            <p className="text-white/80 mt-1.5 text-sm sm:text-base">Join the founding group of KidPath providers and be seen first by Ontario families.</p>
          </div>
          <Link to="/provider/register" data-testid="provider-join-btn"
                className="inline-flex items-center gap-2 bg-kid-teal text-white font-semibold px-7 py-3.5 rounded-full hover:opacity-90 whitespace-nowrap transition-opacity">
            List your practice <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}
